import { forwardRef, type FormEvent, type KeyboardEvent } from "react";

import { Close, Search } from "@mui/icons-material";
import {
  IconButton,
  InputBase,
  Paper,
  type SxProps,
  type Theme,
} from "@mui/material";

// Shared search input — Google-style rounded Paper + InputBase. Visual baseline
// taken from RubiREPORT's ReportV2SearchBar.tsx and RubiALERT's SearchBar.tsx
// (~lines 233-281), which are duplicated across modules today.
//
// Controlled API: parent owns `value` and reacts to `onChange` per-keystroke.
// `onSubmit` fires on Enter so consumers can choose between live-search and
// explicit-submit semantics without forking the component:
//   - Feedback uses `onChange` for live filtering, ignores `onSubmit`.
//   - Reports/Alerts will wire `onSubmit` for explicit-search API calls and
//     still use `onChange` to keep their controlled text state in sync.
//
// Colours follow light/dark mode: the pill uses --search-field-background,
// icons and text use the theme palette (text.primary, text.secondary).
//
// Migration friction notes (RubiREPORT, RubiALERT, RubiINTEL, registry-system):
//
//  1. State lift.
//     Reports' ReportV2SearchBar holds `newSearchText` internally and only
//     pushes to the parent via `onSearch`. SearchBar is purely controlled — the
//     parent must own the text state. Mechanical refactor: lift `newSearchText`
//     into the parent and rename to `searchText`.
//
//  2. SearchBar is the input only, not the whole toolbar.
//     RubiALERT's SearchBar.tsx is a mega-component (input + filters button +
//     sort toggle + image-search button + view tabs + filter chips). This
//     shared component replaces only the input Paper — the rest stays in the
//     consumer (or, better, composes alongside `<FiltersButton>` and future
//     shared toolbar primitives).
//
//  3. `filtersRef` no longer needed for popover anchoring.
//     Today Reports/Alerts put a `filtersRef` on the search-bar container so
//     the legacy `SearchBarFilters` popover can anchor to it. With
//     `<FiltersButton>` (which self-anchors), that ref disappears entirely.
//     If a consumer still needs a DOM ref for measurement, forward it to this
//     component — `ref` lands on the root Paper.

export interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  /** Called when the user presses Enter. Use for explicit-submit search. */
  onSubmit?: () => void;
  placeholder?: string;
  /** Caps the Paper width on large screens. Defaults to 800. */
  maxWidth?: number | string;
  /** Hides the clear (X) icon when false. Defaults to true. */
  clearable?: boolean;
  /** aria-label for the input. Defaults to "Search". */
  ariaLabel?: string;
  /** Optional sx pass-through for the root Paper. */
  sx?: SxProps<Theme>;
  /** Disables the input and clear button. */
  disabled?: boolean;
}

export const SearchBar = forwardRef<HTMLFormElement, SearchBarProps>(
  function SearchBar(
    {
      value,
      onChange,
      onSubmit,
      placeholder = "Search",
      maxWidth = 800,
      clearable = true,
      ariaLabel = "Search",
      sx,
      disabled = false,
    },
    ref,
  ) {
    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter" && onSubmit) {
        event.preventDefault();
        onSubmit();
      }
    };

    return (
      <Paper
        ref={ref}
        component="form"
        role="search"
        onSubmit={(e: FormEvent<HTMLFormElement>) => e.preventDefault()}
        elevation={4}
        sx={{
          p: "2px 4px",
          display: "flex",
          alignItems: "center",
          flex: 1,
          width: "100%",
          maxWidth,
          borderRadius: 1,
          // Same pill as the Reports/Alerts search bars: the app's
          // --search-field-background token (falling back to action.hover where
          // the app doesn't define it), with Paper's dark-mode elevation overlay
          // suppressed so it doesn't wash the token out.
          bgcolor: (theme) =>
            `var(--search-field-background, ${theme.palette.action.hover})`,
          backgroundImage: "none",
          // Outlined in dark mode, matching the Reports/Alerts search fields;
          // transparent in light so the field is the same size in both.
          border: (theme) =>
            `1px solid var(--search-field-border, ${
              theme.palette.mode === "dark"
                ? theme.palette.divider
                : "transparent"
            })`,
          ...sx,
        }}
      >
        <IconButton
          type="button"
          sx={{ p: "10px", color: "text.secondary" }}
          aria-label={ariaLabel}
          tabIndex={-1}
        >
          <Search />
        </IconButton>
        <InputBase
          sx={{ ml: 1, flex: 1, fontSize: "16px", color: "text.primary" }}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          inputProps={{ "aria-label": ariaLabel, type: "search" }}
        />
        {clearable && value && (
          <IconButton
            type="button"
            aria-label="Clear search"
            sx={{ p: "10px", color: "text.secondary" }}
            onClick={() => onChange("")}
            disabled={disabled}
          >
            <Close />
          </IconButton>
        )}
      </Paper>
    );
  },
);
